import { Redirect, Route, Switch } from 'react-router-dom';

import { Permission } from '../../__generated__/graphql';
import { useSession } from '../Auth/SessionProvider';

import AddEventPage from '../Events/AddEventPage';
import ApiPage from '../Projects/ApiPage';
import BillingPage from '../Projects/BillingPage';
import CommunicationLayout from '../Communication/CommunicationLayout';
import ContactsPage from '../Participants/Contacts/ContactsPage';
import DashboardLayout from '../Dashboard/DashboardLayout';
import DashboardPage from '../Dashboard/DashboardPage';
import EditFeaturesPage from '../Projects/EditFeaturesPage';
import EditIntegrationsPage from '../Projects/EditIntegrationsPage';
import EditOrganisationPage from '../Projects/EditOrganisationPage';
import EditPaymentsPage from '../Projects/EditPaymentsPage';
import EditPrivacyPage from '../Projects/EditPrivacyPage';
import EventCouponsPage from '../Events/Event/Coupons/CouponsPage';
import EventDemographicsDashboardPage from '../Dashboard/EventDemographicsDashboardPage';
import EventDiscountsPage from '../Events/Event/Discounts/DiscountsPage';
import EventFieldsPage from '../Events/Event/Fields/FieldsPage';
import EventFundraisingDashboardPage from '../Dashboard/EventFundraisingDashboardPage';
import EventFundraisingSettingsPage from '../Events/Event/Fundraising/FundraisingSettingsPage';
import EventInvitationsPage from '../Events/Event/Invitations/InvitationsPage';
import EventLayout from '../Events/Event/EventLayout';
import EventProductsPage from '../Events/Event/Products/ProductsPage';
import EventRegistrationsDashboardPage from '../Dashboard/EventRegistrationsDashboardPage';
import EventResaleSettingsPage from '../Events/Event/Resale/ResaleSettingsPage';
import EventSettingsPage from '../Events/Event/Settings/SettingsPage';
import EventTicketsPage from '../Events/Event/Tickets/TicketsPage';
import EventsPage from '../Events/EventsPage';
import LotteriesPage from '../Participants/Lotteries/LotteriesPage';
import MailPermissionsPage from '../Communication/MailPermissions/MailPermissionsPage';
import MessagePage from '../Communication/Messages/MessagePage';
import MessagesPage from '../Communication/Messages/MessagesPage';
import NotFoundPage from './Errors/NotFoundPage';
import OrdersPage from '../Participants/Orders/OrdersPage';
import ParticipantAboutPage from '../Participants/Participant/About/AboutPage';
import ParticipantCommunicationPage from '../Participants/Participant/Communication/CommunicationPage';
import ParticipantFieldImportsPage from '../Participants/Imports/ParticipantFieldImportsPage';
import ParticipantNotesPage from '../Participants/Participant/Notes/NotesPage';
import ParticipantOrdersPage from '../Participants/Participant/Orders/OrdersPage';
import ParticipantPageLayout from '../Participants/Participant/Layout';
import ParticipantsLayout from '../Participants/ParticipantsLayout';
import ProjectLayout from '../Projects/ProjectLayout';
import ProtectedRoute from './ProtectedRoute';
import RegistrationsPage from '../Participants/Registrations/RegistrationsPage';
import ReportsPage, { ReportsPageProps } from '../Reports/ReportsPage';
import ScannersPage from '../Participants/Scanners/ScannersPage';
import TeamPage from '../Participants/Team/TeamPage';
import TeamsPage from '../Participants/Team/TeamsPage';
import TicketNumbersPage from '../Participants/TicketNumbers/TicketNumbersPage';
import UsersPage from '../Projects/UsersPage';

export interface ProjectRoutesProps {
  projectId: string;
}

const ProjectRoutes = ({ projectId }: ProjectRoutesProps) => {
  const { hasPermission } = useSession();

  return (
    <Switch>
      <ProtectedRoute
        path="/:projectId/dashboard"
        permissions={[Permission.event_dashboards]}
      >
        <Switch>
          <Route
            path="/:projectId/dashboard/:event?/:tab?"
            render={({ match: { params } }) => (
              <DashboardLayout eventId={params.event} tab={params.tab}>
                <Switch>
                  <Route
                    path="/:projectId/dashboard/:event/demographics"
                    render={() => <EventDemographicsDashboardPage eventId={params.event} />}
                  />
                  <Route
                    path="/:projectId/dashboard/:event/fundraising"
                    render={() => <EventFundraisingDashboardPage eventId={params.event} />}
                  />
                  <Route
                    path="/:projectId/dashboard/:event"
                    render={() => <EventRegistrationsDashboardPage eventId={params.event} />}
                  />
                  <Route
                    path="/:projectId/dashboard"
                    render={() => <DashboardPage />}
                  />
                </Switch>
              </DashboardLayout>
            )}
          />
        </Switch>
      </ProtectedRoute>

      <ProtectedRoute
        path="/:projectId/events"
        permissions={[Permission.edit_events]}
      >
        <Switch>
          <Route
            path="/:projectId/events/add"
            render={() => <AddEventPage />}
          />

          <Route
            path="/:projectId/events/:eventId"
            render={({ match: { params } }) => (
              <EventLayout eventId={params.eventId}>
                <Switch>
                  <Route
                    path="/:projectId/events/:eventId/tickets/:ticketId?"
                    render={({ match: { params } }) => (
                      <EventTicketsPage eventId={params.eventId} ticketId={params.ticketId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/products/:productId?"
                    render={({ match: { params } }) => (
                      <EventProductsPage eventId={params.eventId} productId={params.productId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/invitation-codes/:invitationId?"
                    render={({ match: { params } }) => (
                      <EventInvitationsPage eventId={params.eventId} invitationId={params.invitationId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/discounts/:discountId?"
                    render={({ match: { params } }) => (
                      <EventDiscountsPage eventId={params.eventId} discountId={params.discountId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/coupons/:couponId?"
                    render={({ match: { params } }) => (
                      <EventCouponsPage eventId={params.eventId} couponId={params.couponId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/fields"
                    render={({ match: { params } }) => (
                      <EventFieldsPage eventId={params.eventId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/resale"
                    render={({ match: { params } }) => (
                      <EventResaleSettingsPage eventId={params.eventId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/fundraising"
                    render={({ match: { params } }) => (
                      <EventFundraisingSettingsPage eventId={params.eventId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId/settings"
                    render={({ match: { params } }) => (
                      <EventSettingsPage eventId={params.eventId} />
                    )}
                  />
                  <Route
                    path="/:projectId/events/:eventId"
                    render={({ match: { params: { projectId, eventId } } }) => (
                      <Redirect to={`/${projectId}/events/${eventId}/tickets`} />
                    )}
                  />
                </Switch>
              </EventLayout>
            )}
          />

          <Route
            path="/:projectId/events"
            render={() => <EventsPage />}
          />
        </Switch>
      </ProtectedRoute>

      <ProtectedRoute
        path="/:projectId/participants"
        permissions={[Permission.view_registrations]}
      >
        <Switch>
          <Route
            path="/:projectId/participants"
            render={({ match: { params } }) => (
              <ParticipantsLayout {...params}>
                <Switch>
                  <Route
                    path="/:projectId/participants/teams/:teamId"
                    render={({ match: { params } }) => (
                      <TeamPage teamId={params.teamId} />
                    )}
                  />
                  <Route
                    path="/:projectId/participants/teams"
                    render={() => (
                      <TeamsPage />
                    )}
                  />
                  <Route
                    path="/:projectId/participants/orders"
                    render={() => (
                      <OrdersPage />
                    )}
                  />
                  <Route
                    path="/:projectId/participants/contacts"
                    render={() => (
                      <ContactsPage />
                    )}
                  />
                  <ProtectedRoute
                    permissions={[Permission.participant_logistics]}
                    path="/:projectId/participants/bib-numbers/:ticketNumberSequenceId?"
                    render={({ match: { params } }) => (
                      <TicketNumbersPage {...params} />
                    )}
                  />
                  <ProtectedRoute
                    permissions={[Permission.participant_logistics]}
                    path="/:projectId/participants/scanners/:scannerId?"
                    render={({ match: { params } }) => (
                      <ScannersPage {...params} />
                    )}
                  />
                  <ProtectedRoute
                    permissions={[Permission.participant_logistics]}
                    path="/:projectId/participants/lotteries/:lotteryId?"
                    render={({ match: { params } }) => (
                      <LotteriesPage {...params} />
                    )}
                  />
                  <Route
                    path="/:projectId/participants/imports/:participantFieldImportId?"
                    render={({ match: { params } }) => (
                      <ParticipantFieldImportsPage {...params} />
                    )}
                  />
                  <Route
                    path="/:projectId/participants/:participantId"
                    render={({ match: { params: { participantId } } }) => (
                      <ParticipantPageLayout participantId={participantId}>
                        <Switch>
                          <Route
                            path="/:projectId/participants/:participantId/orders/:orderId?"
                            render={({ match: { params } }) => (
                              <ParticipantOrdersPage
                                orderId={params.orderId}
                                participantId={params.participantId}
                              />
                            )}
                          />
                          <Route
                            path="/:projectId/participants/:participantId/communication"
                            render={({ match: { params } }) => (
                              <ParticipantCommunicationPage
                                participantId={params.participantId}
                              />
                            )}
                          />
                          <Route
                            path="/:projectId/participants/:participantId/notes"
                            render={({ match: { params } }) => (
                              <ParticipantNotesPage
                                participantId={params.participantId}
                              />
                            )}
                          />
                          <Route
                            render={({ match: { params } }) => (
                              <ParticipantAboutPage
                                participantId={params.participantId}
                              />
                            )}
                          />
                        </Switch>
                      </ParticipantPageLayout>
                    )}
                  />

                  <Route
                    path="/:projectId/participants"
                    render={() => {
                      if (!hasPermission(projectId, [Permission.view_registrations])) {
                        // The user cannot view registration, but can manage bib numbers.
                        return <Redirect to={`/${projectId}/participants/bib-numbers`} />;
                      }

                      return <RegistrationsPage />;
                    }}
                  />
                </Switch>
              </ParticipantsLayout>
            )}
          />
        </Switch>
      </ProtectedRoute>

      <ProtectedRoute
        path="/:projectId/communication"
        permissions={[Permission.messages]}
      >
        <Switch>
          <Route
            path={[
              '/:projectId/communication/messages',
              '/:projectId/communication/permissions',
            ]}
            render={() => (
              <CommunicationLayout>
                <Switch>
                  <Route
                    path="/:projectId/communication/messages/:messageId/:route?"
                    render={({ match: { params } }) => (
                      <MessagePage messageId={params.messageId} route={params.route} />
                    )}
                  />
                  <Route
                    path="/:projectId/communication/messages"
                    render={() => (
                      <MessagesPage />
                    )}
                  />
                  <Route
                    path="/:projectId/communication/permissions/:mailPermissionId?"
                    render={({ match: { params } }) => (
                      <MailPermissionsPage {...params} />
                    )}
                  />
                </Switch>
              </CommunicationLayout>
            )}
          />

          <Route
            path="/:projectId/communication"
            render={({ match: { params } }) => (
              <Redirect to={`/${params.projectId}/communication/messages`} />
            )}
          />
        </Switch>
      </ProtectedRoute>

      <ProtectedRoute
        path="/:projectId/reports"
        permissions={[Permission.financial_reports]}
      >
        <Switch>
          <Route
            path="/:projectId/reports/:type?"
            render={({ match: { params } }) => (
              <ReportsPage {...params as ReportsPageProps} />
            )}
          />
        </Switch>
      </ProtectedRoute>

      <ProtectedRoute
        path="/:projectId/project"
        permissions={[Permission.admin]}
      >
        <Switch>
          <ProtectedRoute
            path="/:projectId/project"
            permissions={[Permission.admin]}
            render={({ match: { params: { projectId } } }) => (
              <ProjectLayout>
                <Switch>
                  <Route
                    path="/:projectId/project/organisation"
                    render={() => <EditOrganisationPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/features"
                    render={() => <EditFeaturesPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/payments"
                    render={() => <EditPaymentsPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/integrations"
                    render={() => <EditIntegrationsPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/privacy"
                    render={() => <EditPrivacyPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/platform-fee"
                    render={() => <BillingPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/users"
                    render={() => <UsersPage projectId={projectId} />}
                  />
                  <Route
                    path="/:projectId/project/api/:appId?"
                    render={({ match: { params: { appId } } }) => (
                      <ApiPage projectId={projectId} appId={appId} />
                    )}
                  />
                  <Redirect
                    from="/:projectId/project"
                    to="/:projectId/project/organisation"
                  />
                </Switch>
              </ProjectLayout>
            )}
          />
        </Switch>
      </ProtectedRoute>

      <Route
        path="/:projectId/:path?"
        render={({ match: { params: { path } } }) => {
          if (!path) {
            if (hasPermission(projectId, [Permission.event_dashboards])) {
              return <Redirect to={`/${projectId}/dashboard`} />;
            }

            if (hasPermission(projectId, [Permission.view_registrations])) {
              return <Redirect to={`/${projectId}/participants`} />;
            }

            if (hasPermission(projectId, [Permission.edit_events])) {
              return <Redirect to={`/${projectId}/events`} />;
            }

            if (hasPermission(projectId, [Permission.messages])) {
              return <Redirect to={`/${projectId}/communication`} />;
            }

            if (hasPermission(projectId, [Permission.financial_reports])) {
              return <Redirect to={`/${projectId}/reports`} />;
            }
          }

          return <NotFoundPage />;
        }}
      />
    </Switch>
  );
};

export default ProjectRoutes;
